import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Alert, Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PrintIcon from "@mui/icons-material/Print";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const studyYearLabel = (value) => {
  const n = Number(value);
  if (!Number.isInteger(n) || n < 1) return "—";

  const labels = ["I Year", "II Year", "III Year", "IV Year", "V Year"];
  return labels[n - 1] || `${n} Year`;
};

const getAuthHeaders = () => {
  const token = sessionStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function FeeBooksView({ selectedStudent }) {
  const [rows, setRows] = useState([]);
  const [yearTotals, setYearTotals] = useState([]);
  const [open, setOpen] = useState(false);
  const [printingPdf, setPrintingPdf] = useState(false);
  const [total, setTotal] = useState({
    configured: 0,
    collected: 0,
    concession: 0,
    refund: 0,
    balance: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadFeeBooks = useCallback(async () => {
    if (!selectedStudent?.student_id) {
      setRows([]);
      setYearTotals([]);
      setTotal({
        configured: 0,
        collected: 0,
        concession: 0,
        refund: 0,
        balance: 0,
      });
      setError("");
      return;
    }

    let user;
    try {
      user = JSON.parse(sessionStorage.getItem("user") || "{}");
    } catch {
      setError("Unable to read the current user session.");
      return;
    }

    const clientId = user.client_id;
    const collegeId = user.college_id;

    if (!clientId || !collegeId) {
      setError("Client or college information is missing from the session.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/fee-book-view/${clientId}/${collegeId}/students/${selectedStudent.student_id}`,
        { headers: getAuthHeaders() }
      );

      const result = response.data?.data || {};

      setRows(result.rows || []);
      setYearTotals(result.yearTotals || []);
      setTotal(
        result.total || {
          configured: 0,
          collected: 0,
          concession: 0,
          refund: 0,
          balance: 0,
        }
      );
    } catch (err) {
      console.error("Failed to load fee books:", err);
      setRows([]);
      setYearTotals([]);
      setError(err.response?.data?.message || "Failed to load student fee books.");
    } finally {
      setLoading(false);
    }
  }, [selectedStudent?.student_id]);
  const printFeeDueReport = async () => {
    if (!selectedStudent?.student_id || printingPdf) return;

    const printWindow = window.open("about:blank", "_blank");

    if (!printWindow) {
      setError("Please allow popups to open the Fee Due Report.");
      return;
    }

    setPrintingPdf(true);
    setError("");

    try {
      const user = JSON.parse(sessionStorage.getItem("user") || "{}");
      const { client_id: clientId, college_id: collegeId } = user;

      if (!clientId || !collegeId) {
        throw new Error("Client or college information is missing.");
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/fee-book-view/${clientId}/${collegeId}/students/${selectedStudent.student_id}/fee-due-report`,
        {
          headers: getAuthHeaders(),
          responseType: "blob",
          timeout: 60000,
        }
      );

      const pdfBlob = new Blob([response.data], {
        type: "application/pdf",
      });

      if (!pdfBlob.size) {
        throw new Error("The Fee Due Report PDF is empty.");
      }

      const pdfUrl = URL.createObjectURL(pdfBlob);
      printWindow.location.href = pdfUrl;

      window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 120000);
    } catch (err) {
      printWindow.close();

      console.error("Fee Due Report PDF error:", err);

      let message =
        err?.response?.data?.message || err?.message || "Unable to generate the Fee Due Report.";

      // Error responses may also be returned as a Blob.
      if (err?.response?.data instanceof Blob) {
        try {
          const errorData = JSON.parse(await err.response.data.text());
          message = errorData.message || message;
        } catch {
          // Keep the existing error message.
        }
      }

      setError(message);
    } finally {
      setPrintingPdf(false);
    }
  };
  const columns = useMemo(
    () => [
      {
        field: "academic_year",
        headerName: "Academic Year",
        minWidth: 125,
        flex: 0.9,
      },
      {
        field: "study_year",
        headerName: "Study Year",
        minWidth: 100,
        flex: 0.7,
        valueGetter: (params) => studyYearLabel(params.row.study_year),
      },
      {
        field: "fee_head_name",
        headerName: "Fee Head",
        minWidth: 190,
        flex: 1.5,
      },
      ...[
        ["configured", "Configured Fee"],
        ["collected", "Collected"],
        ["concession", "Concession"],
        ["refund", "Refund"],
        ["balance", "Balance"],
      ].map(([field, headerName]) => ({
        field,
        headerName,
        minWidth: 125,
        flex: 0.9,
        type: "number",
        align: "right",
        headerAlign: "right",
        valueFormatter: (params) => `₹${money(params.value)}`,
        cellClassName:
          field === "collected" ? "fee-collected" : field === "balance" ? "fee-balance" : "",
      })),
    ],
    []
  );

  const summaryColumns = useMemo(
    () => [
      {
        field: "academic_year",
        headerName: "Academic Year",
        minWidth: 135,
        flex: 1,
      },
      {
        field: "study_year",
        headerName: "Study Year",
        minWidth: 105,
        flex: 0.8,
        valueGetter: (params) => studyYearLabel(params.row.study_year),
      },
      ...[
        ["configured", "Configured Fee"],
        ["collected", "Collected"],
        ["concession", "Concession"],
        ["refund", "Refund"],
        ["balance", "Balance"],
      ].map(([field, headerName]) => ({
        field,
        headerName,
        minWidth: 125,
        flex: 1,
        type: "number",
        align: "right",
        headerAlign: "right",
        valueFormatter: (params) => `₹${money(params.value)}`,
      })),
    ],
    []
  );

  return (
    <>
      <Button
        variant="contained"
        fullWidth
        startIcon={<ReceiptLongIcon />}
        disabled={!selectedStudent || loading}
        onClick={async () => {
          setOpen(true);
          await loadFeeBooks();
        }}
        sx={{
          minHeight: 42,
          color: "#fff",
          backgroundColor: "#1976d2",
          fontWeight: 700,
          fontSize: "0.78rem",
          whiteSpace: "normal",
          lineHeight: 1.3,
          px: 0.75,
          "&:hover": {
            backgroundColor: "#1565c0",
          },
        }}
      >
        Show Fee Books
      </Button>

      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          setOpen(false);
        }}
        disableEscapeKeyDown
        maxWidth="xl"
        fullWidth
        fullScreen={false}
        PaperProps={{
          sx: {
            m: { xs: 1, sm: 2 },
            width: { xs: "calc(100% - 16px)", sm: "100%" },
            maxHeight: { xs: "95vh", sm: "90vh" },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#3781e2",
            color: "#fff",
            fontWeight: 700,
            py: 0.5,
            pl: 2,
            pr: 1,
            minHeight: 36,
            boxSizing: "border-box",
          }}
        >
          <Typography component="span" sx={{ fontSize: "1rem", fontWeight: 700 }}>
            STUDENT FEE BOOKS
          </Typography>

          <IconButton
            aria-label="Close fee books"
            onClick={() => setOpen(false)}
            sx={{
              color: "#d32f2f",
              backgroundColor: "#ffebee",
              "&:hover": {
                backgroundColor: "#ffebee",
                color: "#b71c1c",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
            sx={{ mb: 2, mt: 0.5 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#010b12",
                  lineHeight: 1.7,
                  overflowWrap: "anywhere",
                }}
              >
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Name:
                </Box>{" "}
                {selectedStudent?.student_name || selectedStudent?.name || "-"}
                {"  "}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Roll No:
                </Box>{" "}
                {selectedStudent?.student_rollno || "-"}
              </Typography>
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: "nowrap" }}>
                Fee Books: {rows.length}
              </Typography>

              <Button
                variant="contained"
                startIcon={
                  printingPdf ? (
                    <CircularProgress size={17} color="inherit" />
                  ) : (
                    <PictureAsPdfIcon />
                  )
                }
                onClick={printFeeDueReport}
                disabled={printingPdf || loading}
                fullWidth
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minHeight: 40,
                  color: "#fff",
                  backgroundColor: "#1976d2",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  "&:hover": {
                    backgroundColor: "#1565c0",
                  },
                }}
              >
                {printingPdf ? "Generating PDF..." : "Print Fee Due"}
              </Button>
            </Stack>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
                gap: 1.5,
              }}
            >
              <CircularProgress size={28} />
              <Typography variant="body2">Loading fee books...</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ width: "100%", overflowX: "auto" }}>
                <Box sx={{ minWidth: 1000 }}>
                  <DataGrid
                    rows={rows}
                    columns={columns}
                    autoHeight
                    disableRowSelectionOnClick
                    hideFooter
                    density="compact"
                    getRowHeight={() => "auto"}
                    getRowId={(row) =>
                      row.id ??
                      row.fee_book_id ??
                      `${row.academic_year}-${row.study_year}-${row.fee_head_name}`
                    }
                    sx={{
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "#e3f2fd",
                        fontWeight: 700,
                      },
                      "& .MuiDataGrid-columnHeaderTitle": {
                        fontWeight: 700,
                      },
                      "& .MuiDataGrid-cell": {
                        py: 1,
                        whiteSpace: "normal",
                        fontSize: "0.84rem",
                      },
                      "& .fee-collected": {
                        color: "#2e7d32",
                        fontWeight: 700,
                      },
                      "& .fee-balance": {
                        color: "#d32f2f",
                        fontWeight: 700,
                      },
                    }}
                  />
                </Box>
              </Box>

              <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 3, mb: 1 }}>
                Year-wise Totals
              </Typography>

              <Box sx={{ width: "100%", overflowX: "auto" }}>
                <Box sx={{ minWidth: 950 }}>
                  <DataGrid
                    rows={yearTotals.map((row, index) => ({
                      ...row,
                      id: row.id ?? `${row.academic_year}-${row.study_year}-${index}`,
                    }))}
                    columns={summaryColumns}
                    autoHeight
                    hideFooter
                    disableRowSelectionOnClick
                    density="compact"
                    sx={{
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "#e8f5e9",
                        fontWeight: 700,
                      },
                      "& .MuiDataGrid-columnHeaderTitle": {
                        fontWeight: 700,
                      },
                    }}
                  />
                </Box>
              </Box>

              <Paper
                variant="outlined"
                sx={{
                  mt: 2,
                  p: { xs: 1.5, sm: 2 },
                  borderColor: "#1976d2",
                  backgroundColor: "#f5f9ff",
                }}
              >
                <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5 }}>
                  Final Total
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, minmax(0, 1fr))",
                      sm: "repeat(3, minmax(0, 1fr))",
                      lg: "repeat(5, minmax(0, 1fr))",
                    },
                    gap: { xs: 1.5, sm: 2 },
                  }}
                >
                  {[
                    ["Configured Fee", total.configured],
                    ["Collected", total.collected],
                    ["Concession", total.concession],
                    ["Refund", total.refund],
                    ["Balance", total.balance],
                  ].map(([label, value]) => (
                    <Box key={label} sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        {label}
                      </Typography>

                      <Typography
                        variant="body1"
                        fontWeight={800}
                        sx={{
                          overflowWrap: "anywhere",
                          color:
                            label === "Collected"
                              ? "#2e7d32"
                              : label === "Balance"
                              ? "#d32f2f"
                              : "text.primary",
                        }}
                      >
                        ₹{money(value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button
            onClick={() => setOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: "#d32f2f",
              color: "#fff",
              fontWeight: 700,
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

FeeBooksView.propTypes = {
  selectedStudent: PropTypes.shape({
    student_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    student_name: PropTypes.string,
    student_rollno: PropTypes.string,
    student_roll_no: PropTypes.string,
    rollno: PropTypes.string,
    name: PropTypes.string,
  }),
};

FeeBooksView.defaultProps = {
  selectedStudent: null,
};
